import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const NotificationContext = createContext(null);
export const useNotifications = () => useContext(NotificationContext);

const API = '/api';

const NOTIF_SOUND_URL = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVoGAACAgICAgICAgICAgICAgICAgICAgICAgICAhoaJi46QkpWXmZucnp+goKGhoaCfnpybmZeVkpCOi4mGhICAgICAgICAgICAgICAgIB+fXt5eHZ1dHNycXFxcXFycnN0dXZ4eXt9foCAgoSGiYuOkJKVl5mcnp+goaGhoKCfnp2bmZeVkpCOi4mHhIKAgH59e3l4dnV0c3JxcXFxcXJyc3R1dnh5e31+gICChIaJi46QkpWXmZudn5+goaGhoaCfnpybmZeVkpCOi4mGhIKAf317eXh2dXRzcnFxcXFxcnJzdHV2eHl7fX6AgIKEhomLjpCSlZeZm52fn6ChoaGhoJ+enJuZl5WSkI6LiYaEgoB/fXt5eHZ1dHNycXFxcXFycnN0dXZ4eXt9foCAgoSGiIuNj5GUlpeZm52en5+goKCgoJ+enZuZl5WTkY6MioeEgoB/fXt5eHZ1dHNycnFxcXFyc3R1dnh5e31+gICChIaIi42PkZSWl5mbnZ6fn5+goKCgn56dnJqYlpOSkI6LiYaEgoB/fXt5eHZ1dHNycXFxcXJzdHV2eHl7fX6A';

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('rt_notif_sound');
    return saved === null ? true : saved === 'true';
  });
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const audioRef = useRef(null);

  const playSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(NOTIF_SOUND_URL);
        audioRef.current.volume = 0.3;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => { /* autoplay may be blocked */ });
    } catch { /* audio init may fail */ }
  }, [soundEnabled]);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem('rt_notif_sound', String(next));
      return next;
    });
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(data.count);
    } catch (e) { /* non-critical: notification count */ }
  }, []);

  const fetchNotifications = useCallback(async (page = 1) => {
    try {
      const { data } = await axios.get(`${API}/notifications?page=${page}&limit=30`, { withCredentials: true });
      if (page === 1) {
        setNotifications(data.notifications);
      } else {
        setNotifications(prev => [...prev, ...data.notifications]);
      }
      return data;
    } catch (e) { console.error('Failed to fetch notifications:', e); return null; }
  }, []);

  const markRead = useCallback(async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { console.error('Failed to mark notification read:', e); }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { console.error('Failed to mark all read:', e); }
  }, []);

  const deleteNotification = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`, { withCredentials: true });
      setNotifications(prev => {
        const removed = prev.find(n => n.id === id);
        if (removed && !removed.read) setUnreadCount(c => Math.max(0, c - 1));
        return prev.filter(n => n.id !== id);
      });
    } catch (e) { console.error('Failed to delete notification:', e); }
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!user) return;

    const connect = () => {

      const wsProto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      // The session cookie is sent automatically with the handshake.
      const wsUrl = `${wsProto}://${window.location.host}/api/notifications/ws`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Send periodic pings
        const ping = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
        ws._pingInterval = ping;
      };

      ws.onmessage = (evt) => {
        if (evt.data === 'pong') return;
        try {
          const payload = JSON.parse(evt.data);
          if (payload.event === 'notification') {
            const n = payload.data;
            setNotifications(prev => [n, ...prev]);
            setUnreadCount(prev => prev + 1);
            playSound();
            toast(n.title, { description: n.message, duration: 6000 });
          }
        } catch (e) { /* WS message parse error - non-critical */ }
      };

      ws.onclose = () => {
        clearInterval(ws._pingInterval);
        wsRef.current = null;
        reconnectTimer.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => ws.close();
    };

    fetchUnreadCount();
    connect();

    return () => {
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user, fetchUnreadCount, playSound]);

  const value = useMemo(() => ({
    unreadCount, notifications, inboxOpen, setInboxOpen,
    soundEnabled, toggleSound,
    fetchNotifications, markRead, markAllRead, deleteNotification, fetchUnreadCount,
  }), [unreadCount, notifications, inboxOpen, soundEnabled, toggleSound,
    fetchNotifications, markRead, markAllRead, deleteNotification, fetchUnreadCount]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
